import { Box, Stack } from "@mui/material";
import { sanitizeHtml } from "@src/appV2/Knock/lib/sanitizeHtml";
import { useTu } from "@src/appV2/i18n/useTu";
import MoodIllustration from "@src/appV2/redesign/assets/icons-colored/rating-prompt-mood.svg?react";
import { Button } from "@src/appV2/redesign/components/Button";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { ReviewPromptAppreciationStep } from "./ReviewPromptAppreciationStep";
import { ReviewPromptFeedbackStep } from "./ReviewPromptFeedbackStep";

export interface ReviewPromptGuideViewContent {
  title: string;
  body?: string;
  yesButtonText: string;
  noButtonText: string;
  laterButtonText: string;
  /** Feedback-step heading. Defaults to a built-in string when omitted. */
  feedbackTitle?: string;
  /** Feedback-step subtitle rendered under the title. */
  feedbackBody?: string;
  /** Placeholder shown inside the feedback textarea. */
  feedbackPlaceholder?: string;
  /** Primary submit-button label on the feedback step. */
  feedbackSubmitText?: string;
  /** "Contact support" button label on the feedback step. */
  feedbackSupportText?: string;
  /** Tertiary "Cancel" button label on the feedback step. */
  feedbackCancelText?: string;
  /** Appreciation-step heading shown after feedback submit. Defaults to a built-in string. */
  appreciationTitle?: string;
  /** Appreciation-step body rendered under the title. Defaults to a built-in string. */
  appreciationBody?: string;
}

export interface ReviewPromptGuideViewProps {
  content: ReviewPromptGuideViewContent;
  modalState: UseModalState;
  onYesClick: () => void | Promise<void>;
  /** Fired when the user taps No on the mood step. The view transitions to the
   * feedback step internally; the container should record the click. */
  onNoClick: () => void | Promise<void>;
  onLaterClick: () => void | Promise<void>;
  /** Fired when the user submits non-empty feedback. The view then transitions
   * to the appreciation step; the container should record the submission but
   * must NOT close the modal (the appreciation step owns the close). */
  onFeedbackSubmit: (feedback: string) => void | Promise<void>;
  /** Fired when the user closes the appreciation step via Done / backdrop / Escape.
   * Must only close the modal — the decline/submit was already recorded on submit,
   * so this must not re-fire any engagement event. */
  onAppreciationClose: () => void | Promise<void>;
  /** Fired when the user dismisses the feedback step via Cancel / backdrop / Escape.
   * Does NOT fire for Contact support — the dialog stays open behind the in-app
   * browser so the user can still decide when they return. */
  onFeedbackDismiss: () => void | Promise<void>;
  /** Fired when the user taps Contact support on the feedback step. The container
   * opens the support URL but keeps the dialog mounted. */
  onContactSupport: () => void | Promise<void>;
}

type Step = "mood" | "feedback" | "appreciation";

export function ReviewPromptGuideView(props: ReviewPromptGuideViewProps) {
  const { tu } = useTu("ratingPrompt");
  const {
    content,
    modalState,
    onYesClick,
    onNoClick,
    onLaterClick,
    onFeedbackSubmit,
    onAppreciationClose,
    onFeedbackDismiss,
    onContactSupport,
  } = props;

  const [step, setStep] = useState<Step>("mood");
  const [feedbackText, setFeedbackText] = useState("");

  if (step === "appreciation") {
    return (
      <ReviewPromptAppreciationStep
        modalState={modalState}
        title={content.appreciationTitle ?? tu("Thanks for your feedback")}
        body={
          content.appreciationBody ?? tu("We appreciate you taking the time to help us improve.")
        }
        doneText={tu("Done")}
        onClose={async () => {
          await onAppreciationClose();
        }}
      />
    );
  }

  if (step === "feedback") {
    return (
      <ReviewPromptFeedbackStep
        modalState={modalState}
        title={content.feedbackTitle ?? tu("Share your feedback")}
        body={content.feedbackBody ?? tu("Let us know how we can improve.")}
        placeholder={content.feedbackPlaceholder ?? tu("Tell us more...")}
        submitText={content.feedbackSubmitText ?? tu("Submit feedback")}
        supportText={content.feedbackSupportText ?? tu("Contact support")}
        cancelText={content.feedbackCancelText ?? tu("Cancel")}
        value={feedbackText}
        onChange={setFeedbackText}
        onSubmit={async () => {
          // Always advance to the appreciation step once the user submits, even
          // if the fire-and-forget submit handler rejects — never strand the
          // user on the feedback form after they've sent feedback.
          try {
            await onFeedbackSubmit(feedbackText.trim());
          } finally {
            setStep("appreciation");
          }
        }}
        onContactSupport={async () => {
          await onContactSupport();
        }}
        onDismiss={async () => {
          await onFeedbackDismiss();
        }}
      />
    );
  }

  return (
    <MoodStep
      content={content}
      modalState={modalState}
      onYesClick={onYesClick}
      onNoClick={async () => {
        await onNoClick();
        setStep("feedback");
      }}
      onLaterClick={onLaterClick}
    />
  );
}

interface MoodStepProps {
  content: ReviewPromptGuideViewContent;
  modalState: UseModalState;
  onYesClick: () => void | Promise<void>;
  onNoClick: () => void | Promise<void>;
  onLaterClick: () => void | Promise<void>;
}

function MoodStep(props: MoodStepProps) {
  const { content, modalState, onYesClick, onNoClick, onLaterClick } = props;
  const { title, body, yesButtonText, noButtonText, laterButtonText } = content;

  return (
    <Dialog
      modalState={modalState}
      showCloseButton={false}
      width={360}
      // Escape + backdrop click route to onLaterClick — same intent as the
      // Ask me later button, so the user picks up the dismiss-backoff cooldown
      // instead of leaving the prompt re-targetable next session.
      onClose={async () => {
        await onLaterClick();
      }}
    >
      <Stack alignItems="center" spacing={3}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            paddingTop: theme.spacing(2),
            "& > svg": {
              maxWidth: "160px",
              height: "auto",
            },
          })}
        >
          <MoodIllustration aria-hidden="true" />
        </Box>

        <Stack alignItems="center" spacing={1}>
          <Text
            semibold
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.base?.strong ?? theme.palette.text.primary,
              textAlign: "center",
              wordBreak: "break-word",
              textWrap: "balance",
            })}
          >
            {title}
          </Text>

          {body && (
            <Text
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.base?.base ?? theme.palette.text.secondary,
                textAlign: "center",
                wordBreak: "break-word",
              })}
              dangerouslySetInnerHTML={sanitizeHtml({ potentiallyDangerousHtml: body })}
            />
          )}
        </Stack>

        <Stack spacing={2} sx={{ width: "100%" }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="primary"
              sx={{ flex: 1 }}
              onClick={async () => {
                await onYesClick();
              }}
            >
              {yesButtonText}
            </Button>
            <Button
              variant="outlined"
              sx={{ flex: 1 }}
              onClick={async () => {
                await onNoClick();
              }}
            >
              {noButtonText}
            </Button>
          </Stack>
          <Button
            fullWidth
            variant="link"
            onClick={async () => {
              await onLaterClick();
            }}
          >
            {laterButtonText}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
